/**
 * PWA Encryption Utilities
 * 
 * AES-GCM 256-bit encryption with PBKDF2 key derivation
 * Security-first approach with tenant isolation and ephemeral keys
 */

const toArrayBuffer = (view: Uint8Array): ArrayBuffer => {
  const { buffer, byteOffset, byteLength } = view
  if (buffer instanceof ArrayBuffer && byteOffset === 0 && byteLength === buffer.byteLength) {
    return buffer as ArrayBuffer
  }

  const copy = new Uint8Array(byteLength)
  copy.set(new Uint8Array(buffer, byteOffset, byteLength))
  return copy.buffer
}

// Encryption configuration
export const ENCRYPTION_CONFIG = {
  algorithm: 'AES-GCM' as const,
  keyLength: 256,
  ivLength: 96, // 96-bit IV for GCM mode
  iterations: 100000, // High iteration count for PBKDF2
  hashAlgorithm: 'SHA-256' as const,
  tagLength: 128 // Authentication tag length
};

// Error types
export class EncryptionError extends Error {
  constructor(message: string, public readonly cause?: Error) {
    super(message);
    this.name = 'EncryptionError';
  }
}

export class KeyDerivationError extends EncryptionError {
  constructor(message: string, cause?: Error) {
    super(message, cause);
    this.name = 'KeyDerivationError';
  }
}

// Encrypted data container
export interface EncryptedData {
  data: ArrayBuffer;
  iv: Uint8Array;
  version: number;
}

// Key derivation parameters
export interface KeyDerivationParams {
  tenantId: string;
  userId: string;
  deviceSalt: string;
}

// Encryption key manager for tenant isolation
export class EncryptionKeyManager {
  private keys = new Map<string, CryptoKey>();
  private deviceSalt: string | null = null;

  constructor() {
    this.deviceSalt = this.getOrCreateDeviceSalt();
  }

  private generateRandomSalt(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    // Fallback for environments without Web Crypto (should be rare)
    return Array.from({ length: 32 }, () => Math.floor(Math.random() * 256)
      .toString(16)
      .padStart(2, '0')
    ).join('');
  }

  /**
   * Get or create device salt for this browser
   */
  private getOrCreateDeviceSalt(): string {
    const storageKey = 'scopax_device_salt';
    const hasBrowserStorage =
      typeof window !== 'undefined' &&
      typeof window.localStorage !== 'undefined';

    if (!hasBrowserStorage) {
      return this.generateRandomSalt();
    }

    try {
      let salt = window.localStorage.getItem(storageKey);

      if (!salt) {
        salt = this.generateRandomSalt();
        window.localStorage.setItem(storageKey, salt);
      }

      return salt;
    } catch {
      return this.generateRandomSalt();
    }
  }

  /**
   * Derive encryption key for tenant and user
   */
  async deriveKey(tenantId: string, userId: string): Promise<CryptoKey> {
    if (!this.deviceSalt) {
      throw new KeyDerivationError('Device salt not available');
    }

    const cacheKey = `${tenantId}:${userId}`;
    
    // Return cached key if available
    const cachedKey = this.keys.get(cacheKey);
    if (cachedKey) {
      return cachedKey;
    }

    try {
      // Prepare key material
      const keyMaterial = `${userId}:${tenantId}:${this.deviceSalt}`;
      const salt = new TextEncoder().encode(`scopax-pwa-${tenantId}`);

      // Import key material
      const importedKey = await crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(keyMaterial),
        'PBKDF2',
        false,
        ['deriveKey']
      );

      // Derive encryption key
      const derivedKey = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt,
          iterations: ENCRYPTION_CONFIG.iterations,
          hash: ENCRYPTION_CONFIG.hashAlgorithm
        },
        importedKey,
        {
          name: ENCRYPTION_CONFIG.algorithm,
          length: ENCRYPTION_CONFIG.keyLength
        },
        false,
        ['encrypt', 'decrypt']
      );

      // Cache the key
      this.keys.set(cacheKey, derivedKey);
      
      return derivedKey;
    } catch (error) {
      throw new KeyDerivationError(
        `Failed to derive encryption key for tenant ${tenantId}`,
        error as Error
      );
    }
  }

  /**
   * Get cached key for tenant and user
   */
  getKey(tenantId: string, userId: string): CryptoKey | undefined {
    const cacheKey = `${tenantId}:${userId}`;
    return this.keys.get(cacheKey);
  }

  /**
   * Clear keys for specific tenant (on tenant switch)
   */
  clearTenantKeys(tenantId: string): void {
    const keysToDelete = Array.from(this.keys.keys()).filter(key => 
      key.startsWith(`${tenantId}:`)
    );
    
    keysToDelete.forEach(key => this.keys.delete(key));
  }

  /**
   * Clear all keys (on logout)
   */
  clearAllKeys(): void {
    this.keys.clear();
  }

  /**
   * Check if key exists for tenant and user
   */
  hasKey(tenantId: string, userId: string): boolean {
    const cacheKey = `${tenantId}:${userId}`;
    return this.keys.has(cacheKey);
  }

  /**
   * Get device salt (for testing/debugging)
   */
  getDeviceSalt(): string | null {
    return this.deviceSalt;
  }
}

// Global key manager instance
export const keyManager = new EncryptionKeyManager();

/**
 * Encrypt data using AES-GCM
 */
export async function encryptData(
  data: unknown,
  key: CryptoKey
): Promise<EncryptedData> {
  try {
    // Generate random IV
    const iv = crypto.getRandomValues(new Uint8Array(ENCRYPTION_CONFIG.ivLength / 8));
    
    // Encode data as JSON
    const jsonData = JSON.stringify(data);
    const encodedData = new TextEncoder().encode(jsonData);

    // Encrypt data
    const encrypted = await crypto.subtle.encrypt(
      {
        name: ENCRYPTION_CONFIG.algorithm,
        iv: toArrayBuffer(iv),
      },
      key,
      encodedData
    );

    return {
      data: encrypted,
      iv,
      version: 1 // For future encryption version support
    };
  } catch (error) {
    throw new EncryptionError(
      'Failed to encrypt data',
      error as Error
    );
  }
}

/**
 * Decrypt data using AES-GCM
 */
export async function decryptData<T = unknown>(
  encryptedData: EncryptedData,
  key: CryptoKey
): Promise<T> {
  try {
    // Decrypt data
    const decrypted = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_CONFIG.algorithm,
        iv: toArrayBuffer(encryptedData.iv),
      },
      key,
      encryptedData.data
    );

    // Decode and parse JSON
    const jsonString = new TextDecoder().decode(decrypted);
    return JSON.parse(jsonString) as T;
  } catch (error) {
    throw new EncryptionError(
      'Failed to decrypt data',
      error as Error
    );
  }
}

/**
 * High-level encrypt function with key derivation
 */
export async function encryptForTenant<T = unknown>(
  data: T,
  tenantId: string,
  userId: string
): Promise<ArrayBuffer> {
  try {
    const key = await keyManager.deriveKey(tenantId, userId);
    const encrypted = await encryptData(data, key);
    
    // Combine IV and encrypted data for storage
    const combined = new Uint8Array(
      4 + // version (4 bytes)
      1 + // IV length (1 byte)
      encrypted.iv.length + // IV
      encrypted.data.byteLength // encrypted data
    );
    
    const view = new DataView(combined.buffer);
    let offset = 0;
    
    // Write version
    view.setUint32(offset, encrypted.version);
    offset += 4;
    
    // Write IV length
    view.setUint8(offset, encrypted.iv.length);
    offset += 1;
    
    // Write IV
    combined.set(encrypted.iv, offset);
    offset += encrypted.iv.length;
    
    // Write encrypted data
    combined.set(new Uint8Array(encrypted.data), offset);
    
    return combined.buffer;
  } catch (error) {
    throw new EncryptionError(
      `Failed to encrypt data for tenant ${tenantId}`,
      error as Error
    );
  }
}

/**
 * High-level decrypt function with key derivation
 */
export async function decryptForTenant<T = unknown>(
  encryptedBuffer: ArrayBuffer,
  tenantId: string,
  userId: string
): Promise<T> {
  try {
    const key = await keyManager.deriveKey(tenantId, userId);
    
    // Parse combined buffer
    const view = new DataView(encryptedBuffer);
    let offset = 0;
    
    // Read version
    const version = view.getUint32(offset);
    offset += 4;
    
    // Read IV length
    const ivLength = view.getUint8(offset);
    offset += 1;
    
    // Read IV
    const iv = new Uint8Array(encryptedBuffer, offset, ivLength);
    offset += ivLength;
    
    // Read encrypted data
    const data = encryptedBuffer.slice(offset);
    
    const encryptedData: EncryptedData = {
      data,
      iv,
      version
    };
    
    return await decryptData<T>(encryptedData, key);
  } catch (error) {
    throw new EncryptionError(
      `Failed to decrypt data for tenant ${tenantId}`,
      error as Error
    );
  }
}

/**
 * Utility to check if Web Crypto is available
 */
export function isWebCryptoAvailable(): boolean {
  return (
    typeof crypto !== 'undefined' &&
    typeof crypto.subtle !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  );
}

/**
 * Utility to get encryption info for debugging
 */
export function getEncryptionInfo() {
  return {
    available: isWebCryptoAvailable(),
    algorithm: ENCRYPTION_CONFIG.algorithm,
    keyLength: ENCRYPTION_CONFIG.keyLength,
    iterations: ENCRYPTION_CONFIG.iterations,
    deviceSalt: keyManager.getDeviceSalt()?.slice(0, 8) + '...' // Only show first 8 chars
  };
}
